/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { Input } from 'reactstrap';
import { ColumnInputProps } from '../../InputProps';
import { getSingleRowTableAddon } from '../tableUtils';
import { getFullName } from '@utils/user';
import { CollaborationEditor } from '@features/rich-text-editor';
import { MappedConnectionWrapper, usePopulateInputFromConnection } from '@features/assistant';
import { TextAreaWithAIDraft } from '@features/ai/TextAreaWithAiDraft';
import { useAIAssistantContext } from '@features/assistant/ai-assistant/AIAssistantContext';
import { useTableInputContext } from '@features/survey/form/input/table/TableInputContext';
import { AI_MESSAGE } from '@constants/ai';
import { isExcludedFromAiProcessing } from '@utils/initiative-universal-tracker';

export default function TextInput(props: ColumnInputProps) {
  const {
    isDisabled,
    column,
    inputColumn,
    handleFocus,
    updateColumn,
    placeholder,
    label,
    currentUser,
    documentId,
    initiativeUtr,
  } = props;

  const currentValue = inputColumn.value || '';
  const { beforeAddon, afterAddon, belowInputAddon } = getSingleRowTableAddon({ ...props, columnCode: column.code });

  const onChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    e.preventDefault();
    const { name, value } = e.target;
    return updateColumn({
      code: name,
      value: value === '' ? undefined : value,
    });
  };

  usePopulateInputFromConnection({
    inputChangeHandler: ({ value, source }) => {
      updateColumn({ value, code: column.code, source });
    },
    valueListCode: column.code,
  });

  const { formRow } = useTableInputContext();
  const { getTextDraft, canAccessAIMetricAssistant } = useAIAssistantContext();

  const disabled = isDisabled?.(props);
  const maxLength = Number(column.validation?.max);

  if (props.isDraft && props.providerFactory && documentId) {
    return (
      <div className='mb-3'>
        {label}
        <div className='position-relative w-100'>
          {beforeAddon?.element || null}
          <CollaborationEditor
            className='text-dark'
            disabled={disabled}
            username={currentUser ? getFullName(currentUser) : undefined}
            providerFactory={props.providerFactory}
            documentId={`${documentId}.column.${column.code}`}
            users={props.users}
          />
          {afterAddon?.element || null}
        </div>
        {belowInputAddon?.element || null}
      </div>
    );
  }

  if (maxLength > 1 && maxLength < 100) {
    return (
      <div className='mb-3'>
        {label}
        <div className='position-relative w-100'>
          {beforeAddon?.element || null}
          <Input
            className='styled-input'
            autoComplete='off'
            type='text'
            id={column.code}
            name={column.code}
            value={currentValue}
            placeholder={placeholder}
            disabled={!!disabled}
            required={true}
            onFocus={handleFocus}
            onClick={handleFocus}
            onChange={onChange}
          />
          {afterAddon?.element || null}
        </div>
        {belowInputAddon?.element || null}
      </div>
    );
  } else {
    return (
      <MappedConnectionWrapper valueListCode={column.code}>
        <div className='mb-3'>
          {label}
          <div className='position-relative w-100'>
            {beforeAddon?.element || null}
            <TextAreaWithAIDraft
              key={`inputfactor-textarea-ai-${column.code}`}
              className='styled-input'
              rows={5}
              id={column.code}
              name={column.code}
              value={currentValue}
              placeholder={placeholder}
              disabled={!!disabled}
              required={true}
              onFocus={handleFocus}
              onClick={handleFocus}
              onChange={(data, _editorState) => updateColumn({ code: column.code, value: data })}
              getDraft={() => getTextDraft(column.code, formRow)}
              canUseAIDraft={canAccessAIMetricAssistant}
              draftConfig={
                isExcludedFromAiProcessing(initiativeUtr)
                  ? { disabled: true, tooltip: AI_MESSAGE.EXCLUDED_METRIC_SINGULAR }
                  : undefined
              }
            />
            {afterAddon?.element || null}
          </div>
          {belowInputAddon?.element || null}
        </div>
      </MappedConnectionWrapper>
    );
  }
}
